import isEmpty from 'lodash/isEmpty';

/* global localStorage, FormData */
const personalizationLocalStorageKey = 'qec-personalization';

export default class Personalization {
	/**
	 * Initializes class
	 */
	init() {
		document.addEventListener( 'DOMContentLoaded', () => {
			const personalizationField = document.querySelector( '.js-personalization-for-registration-field' );

			if ( personalizationField ) {
				const personalizationData = localStorage.getItem( personalizationLocalStorageKey );

				if ( ! personalizationData ) {
					return;
				}

				personalizationField.value = personalizationData;
			}
		} );

		/**
		 * qalaElasticCore.action.doQuery
		 *
		 * AddAction Hook to call the elastic search app and do the query.
		 * It will doAction for the results and the Info meta.
		 */
		wp.hooks.addAction( 'qalaElasticCore.action.savePersonalization', 'qalaElasticCore', this.savePersonalization.bind( this ) );

		// Add the function to read personalizations to the window.QalaElasticCore object.
		window.QalaElasticCore.readAllPersonalizations = this.readAllPersonalizations.bind( this );
		window.QalaElasticCore.readPersonalizations = this.readPersonalizations.bind( this );
		window.QalaElasticCore.readLastPersonalization = this.readLastPersonalization.bind( this );

		// Apply all personalizations, only if not disabled.
		// Search for "qala_elastic_core_disable_personalization" PHP filter to change this in your project.
		if ( ! window.QalaElasticCore?.personalizationConfig?.disablePersonalization ) {
			wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'qalaElasticCore_personalization', query => {
				/**
				 * Check if personalization is enabled. This can be hooked into to disable it for a specific Results block / page.
				 *
				 * @param {boolean} isPersonalizationEnabled
				 * @return {boolean} Is personalization enabled or not.
				 */
				const isPersonalizationEnabled = wp.hooks.applyFilters( 'qalaElasticCore.filter.isPersonalizationEnabled', true );

				if ( ! isPersonalizationEnabled ) {
					return query;
				}

				const personalizations = this.readAllPersonalizations();

				for ( const personalization of personalizations ) {
					if ( ! personalization || ! personalization.key || ! personalization.data ) {
						continue;
					}

					const personalizationValue = this.concatPersonalization( Object.values( personalization.data ) );

					// If we don't have a personalization value (i.e. the user didn't select anything)
					// we don't to add anything to query, otherwise it will just return empty results.
					if ( isEmpty( personalizationValue ) ) {
						continue;
					}

					if ( window.QalaElasticCore?.personalizationConfig?.showNonPersonalizedItems ) {
						query.options.filters.all.push( {
							any: [
								{ is_personalized: 'false' },
								{ [ personalization.key ]: this.concatPersonalization( Object.values( personalization.data ) ) },
							],
						} );
					} else {
						query.options.filters.all.push(
							{ [ personalization.key ]: this.concatPersonalization( Object.values( personalization.data ) ) },
						);
					}
				}

				return query;
			} );
		}
	}

	/**
	 * Saves a personalisation. This is called as callback on qalaElasticCore.action.savePersonalization action
	 *
	 * @param {Object} personalizationData
	 * @return {void}
	 */
	async savePersonalization( personalizationData ) {
		// Validate personalization data.
		let parsedData = {};
		let response = {};
		let isDataValid = false;
		try {
			parsedData = this.parseData( personalizationData );
			isDataValid = true;
		} catch ( e ) {
			response = { isSuccess: false, message: e }; /* eslint-disable-line no-console */
		}

		if ( isDataValid && this.isUserLoggedIn() ) {
			response = await this.savePersonalizationForLoggedIn( parsedData );
		} else if ( isDataValid ) {
			response = this.savePersonalizationForLoggedOut( parsedData );
		}

		/**
		 * qalaElasticCore.action.afterSavePersonalization
		 *
		 * This action Will be fired after the personalization has been saved.
		 *
		 * @param {{isSuccess: boolean, message: string}}
		 */
		wp.hooks.doAction( 'qalaElasticCore.action.afterSavePersonalization', response );
	}

	/**
	 * Reads all currently defined personalizations for the user (if he has any)
	 *
	 * @return {Array} Array of all user's personalizations.
	 */
	readAllPersonalizations() {
		return Object.keys( window.QalaElasticCore.personalizations ).map( key => {
			return { key, data: this.readLastPersonalization( key ) };
		} );
	}

	/**
	 * Reads a single personalization, returns all stored values (last is the most recent one,
	 * should be used as LIFO - last in first out).
	 *
	 * @param {string} key Key for the personalization.
	 * @return {Array} Personalized data
	 */
	readPersonalizations( key ) {
		let personalizedData = null;

		if ( this.isUserLoggedIn() ) {
			personalizedData = this.getPersonalizationForLoggedIn( key );
		} else {
			personalizedData = this.getPersonalizationForLoggedOut( key );
		}

		/**
		 * qalaElasticCore.filter.userPersonalizations
		 *
		 * This filter allows you to filter all user's personalizations for a given key.
		 */
		return wp.hooks.applyFilters( 'qalaElasticCore.filter.userPersonalizations', personalizedData, key );
	}

	/**
	 * Returns the last (most recent) personalization for a given key.
	 *
	 * @param {string} key Key of the personalization.
	 * @return {Array} Personalized data
	 */
	readLastPersonalization( key ) {
		const personalizedData = this.readPersonalizations( key );
		return personalizedData.length > 0 ? personalizedData[ personalizedData.length - 1 ] : [];
	}

	/**
	 * Parse and validate that personalization data received is actually registered / and matches the format / contains all data.
	 *
	 * @param {Object} personalizationData
	 * @return {{key: string, data: Object}} Returns the parse data.
	 *
	 * @throws When personalizationData is invalid.
	 */
	parseData( personalizationData ) {
		if ( ! personalizationData || typeof personalizationData !== 'object' ) {
			throw 'Unable to save personalization, personalizationData isn\t an object.';
		}

		const keys = Object.keys( personalizationData );

		if ( keys.length !== 1 ) {
			throw 'Unable to save personalization, received more than 1 key (name of personalization). Please send only 1 at a time.';
		}

		const key = keys[ 0 ];
		const data = personalizationData[ key ];

		// Validate that key is in fact one of allowed personalizations
		if ( ! window.QalaElasticCore || ! window.QalaElasticCore.personalizations || ! window.QalaElasticCore.personalizations[ key ] ) {
			throw `Unable to validate that personalization "${ key }" has been defined.`;
		}

		// Now validate that all values are set.
		window.QalaElasticCore.personalizations[ key ].forEach( fieldName => {
			if ( ! data[ fieldName ] ) {
				throw `Haven't received ${ fieldName } in ${ key } personalization, aborting`;
			}
		} );

		return {
			key,
			data,
		};
	}

	/**
	 * Used when user is logged out.
	 *
	 * @param {{key: string, data: Object}} parsedData
	 * @return {{isSuccess: boolean, message: string}} Returns a response object.
	 */
	async savePersonalizationForLoggedIn( parsedData ) {
		const ajaxUrl = window.QalaElasticCore.ajaxUrl; // This would be defined by wp_localize_script in WordPress

		const data = new FormData();
		data.append( 'action', 'save_personalization_to_user_profile' );
		data.append( 'personalization_data', JSON.stringify( parsedData ) );
		data.append( 'nonce', window.QalaElasticCore?.personalizationConfig?.ajaxNonce || 'missing-nonce' );

		const requestOptions = {
			method: 'POST',
			credentials: 'same-origin',
			body: new URLSearchParams( data ),
		};

		try {
			const response = await fetch( ajaxUrl, requestOptions );
			const parsedResponse = await response.json();

			if ( parsedResponse.success ) {
				return {
					isSuccess: true,
					message: 'Successfully saved personalization to user profile',
				};
			}

			return {
				isSuccess: false,
				message: parsedResponse?.data?.message || 'Unknown error',
			};
		} catch ( error ) {
			return {
				isSuccess: false,
				message: `Ajax request error: ${ error }`,
			};
		}
	}

	/**
	 * Used when user is logged out.
	 *
	 * @param {{key: string, data: Object}} parsedData
	 * @return {{isSuccess: boolean, message: string}} Returns a response object.
	 */
	savePersonalizationForLoggedOut( parsedData ) {
		const { key, data } = parsedData;

		// Check if there's existing data in localStorage
		const existingData = JSON.parse( localStorage.getItem( personalizationLocalStorageKey ) );

		// If no existing data, save the data to localStorage
		if ( ! existingData ) {
			const newData = [
				{
					key,
					data: [ data ],
				},
			];

			localStorage.setItem( personalizationLocalStorageKey, JSON.stringify( newData ) );
			return {
				isSuccess: true,
				message: 'Successfully saved data to localStorage',
			};
		}

		// Check if there's already data for this key.
		const relevantPersonalizations = existingData.find( specificPersonalization => specificPersonalization.key === key );
		if ( ! relevantPersonalizations ) {
			existingData.push( { key, data: [ data ] } );

			localStorage.setItem( personalizationLocalStorageKey, JSON.stringify( existingData ) );
			return {
				isSuccess: true,
				message: 'Successfully saved data to localStorage',
			};
		}

		// Check for duplicate data
		const isDuplicate = relevantPersonalizations.data.some( existingItem => {
			return JSON.stringify( existingItem ) === JSON.stringify( data );
		} );

		// If the new data is duplicate, we need to remove it from existing position, and just push it to the end of the array.
		if ( isDuplicate ) {
			relevantPersonalizations.data = relevantPersonalizations.data.filter( existingItem => JSON.stringify( existingItem ) !== JSON.stringify( data ) );
		}

		// Set the relevant personalizations back on existing data.
		relevantPersonalizations.data.push( data );
		localStorage.setItem( personalizationLocalStorageKey, JSON.stringify( existingData ) );

		return {
			isSuccess: true,
			message: 'Successfully saved data to localStorage',
		};
	}

	/**
	 * Returns all saved personalizations for a logged in user (from user profile, added to localize). Empty
	 * array if there's none.
	 *
	 * @param {string} key Key of the personalization.
	 * @return {Array} All stored personalizations for a key from user profile.
	 */
	getPersonalizationForLoggedIn( key ) {
		if ( ! window.QalaElasticCore.userPersonalizations || ! Array.isArray( window.QalaElasticCore.userPersonalizations ) ) {
			return [];
		}

		const relevantPersonalizations = window.QalaElasticCore.userPersonalizations.find( personalizations => personalizations && personalizations.key && personalizations.key === key );
		return relevantPersonalizations ? relevantPersonalizations.data : [];
	}

	/**
	 * Returns all saved personalizations for a logged out user (from localStorage). Empty
	 * array if there's none.
	 *
	 * @param {string} key Key of the personalization.
	 * @return {Array} All stored personalizations for a key from local storage.
	 */
	getPersonalizationForLoggedOut( key ) {
		const existingData = JSON.parse( localStorage.getItem( personalizationLocalStorageKey ) );

		if ( ! existingData ) {
			return [];
		}

		const relevantPersonalizations = existingData.find( ( personalizations => personalizations && personalizations.key && personalizations.key === key ) );
		return relevantPersonalizations ? relevantPersonalizations.data : [];
	}

	/**
	 * Checks if the current user is logged in.
	 *
	 * @return {boolean} Is user logged in or not.
	 */
	isUserLoggedIn() {
		return document.querySelector( 'body' ).classList.contains( 'logged-in' );
	}

	/**
	 * Concats the personalization into a unique single value. This should match the method in DocumentApi that's passed on to
	 * `qala_elastic_core_add_personalization_to_document` filter (it just concatenates all values with a space between them)
	 *
	 * @param {Array} data Personalization data to concatenate.
	 * @return {string} Concatenated data.
	 */
	concatPersonalization( data ) {
		let fullValue = '';

		data.forEach( singleValue => {
			return fullValue += singleValue + ' ';
		} );

		return fullValue.trim();
	}
}

